/**
 * Cart Drawer Subscription Banner - minimal modal open/close.
 * Uses data attributes from `snippets/cart-drawer-subscription-banner.liquid`.
 */
(function () {
  const SELECTORS = {
    banner: '[data-cart-subscription-banner]',
    openBtn: '[data-cart-subscription-open]',
    closeBtn: '[data-cart-subscription-close]',
    modal: '.cart-subscription-modal',
    panel: '.cart-subscription-modal__panel',
    optionBtn: '.cart-subscription-modal__option[data-selling-plan-id]',
    error: '[data-cart-subscription-error]',
  };

  function getModalFromBanner(el) {
    const banner = el.closest(SELECTORS.banner);
    if (!banner) return null;
    const modalId = banner.getAttribute('data-modal-id');
    return modalId ? document.getElementById(modalId) : null;
  }

  function openModal(modal) {
    if (!modal) return;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    const focusEl = modal.querySelector(SELECTORS.closeBtn);
    if (focusEl) focusEl.focus({ preventScroll: true });
  }

  function closeModal(modal) {
    if (!modal) return;
    modal.setAttribute('aria-hidden', 'true');
    modal.hidden = true;
  }

  async function postJson(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `Request failed: ${res.status}`);
    }
    return await res.json();
  }

  function setError(modal, message) {
    const el = modal ? modal.querySelector(SELECTORS.error) : null;
    if (!el) return;
    if (!message) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.textContent = message;
  }

  function publishCartUpdate() {
    try {
      // Dawn-style pubsub (used by cart-drawer.js in many themes)
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-subscription-banner' });
        return;
      }
    } catch (e) {
      // ignore
    }
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`Cart fetch failed: ${res.status}`);
    return await res.json();
  }

  function replaceOrAppendFreeWithValue(existing, oldId, newId) {
    const oldStr = String(oldId);
    const newStr = String(newId);
    const base = (existing || '').trim();
    if (!base) return newStr;
    if (base.includes(newStr)) return base;
    if (base.includes(oldStr)) return base.replace(oldStr, newStr);
    return `${base},${newStr}`;
  }

  async function keepFreebiesLinked({ oldVariantId, newVariantId }) {
    if (!oldVariantId || !newVariantId) return;
    if (String(oldVariantId) === String(newVariantId)) return;

    const cart = await fetchCart();
    const freebies = (cart.items || []).filter((it) => {
      const fw = it && it.properties && it.properties.free_with;
      return fw && String(fw).includes(String(oldVariantId));
    });

    await Promise.all(
      freebies.map((freebie) => {
        const props = freebie.properties || {};
        const nextFreeWith = replaceOrAppendFreeWithValue(props.free_with, oldVariantId, newVariantId);
        return postJson('/cart/change.js', {
          id: freebie.key,
          quantity: freebie.quantity,
          properties: { ...props, free_with: nextFreeWith },
        });
      })
    );
  }

  async function swapToSubscription({ lineKey, oldVariantId, newVariantId, qty, sellingPlanId }) {
    // 1) add subscription version FIRST
    await postJson('/cart/add.js', {
      items: [
        {
          id: Number(newVariantId),
          quantity: Number(qty) || 1,
          selling_plan: Number(sellingPlanId),
        },
      ],
    });

    // 2) re-link freebie items (gift) to the new subscription variant id
    // your cart drawer pairs freebies via `child_item.properties.free_with contains item.id`
    await keepFreebiesLinked({ oldVariantId, newVariantId });

    // 3) remove existing one-time line (by key)
    await postJson('/cart/change.js', { id: lineKey, quantity: 0 });
  }

  document.addEventListener('click', async (e) => {
    // Close when clicking outside the panel (backdrop area)
    const openModalEl = e.target.closest(SELECTORS.modal);
    if (openModalEl && !e.target.closest(SELECTORS.panel) && !e.target.closest(SELECTORS.openBtn)) {
      // allow explicit close buttons/overlay handler below too; this is the "outside panel" close
      // (don't close if the click is inside panel content)
      e.preventDefault();
      closeModal(openModalEl);
      return;
    }

    const openBtn = e.target.closest(SELECTORS.openBtn);
    if (openBtn) {
      e.preventDefault();
      openModal(getModalFromBanner(openBtn));
      return;
    }

    const closeBtn = e.target.closest(SELECTORS.closeBtn);
    if (closeBtn) {
      e.preventDefault();
      closeModal(closeBtn.closest(SELECTORS.modal));
      return;
    }

    const optionBtn = e.target.closest(SELECTORS.optionBtn);
    if (!optionBtn) return;

    e.preventDefault();
    const modal = optionBtn.closest(SELECTORS.modal);
    if (!modal) return;

    // find owning banner via modal id
    const banner = document.querySelector(`${SELECTORS.banner}[data-modal-id="${modal.id}"]`);
    if (!banner) return;

    setError(modal, '');

    const lineKey = banner.getAttribute('data-line-key');
    const oldVariantId = banner.getAttribute('data-variant-id');
    const qty = banner.getAttribute('data-qty');
    const sellingPlanId = optionBtn.getAttribute('data-selling-plan-id');
    const relatedVariantId = optionBtn.getAttribute('data-related-variant-id');
    const newVariantId = relatedVariantId || oldVariantId;

    if (!lineKey || !oldVariantId || !newVariantId || !sellingPlanId) {
      setError(modal, 'Unable to apply subscription. Please try again.');
      return;
    }

    const optionButtons = modal.querySelectorAll(SELECTORS.optionBtn);
    optionButtons.forEach((b) => (b.disabled = true));

    try {
      await swapToSubscription({ lineKey, oldVariantId, newVariantId, qty, sellingPlanId });
      closeModal(modal);
      publishCartUpdate();
    } catch (err) {
      setError(modal, 'Something went wrong. Please try again.');
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      optionButtons.forEach((b) => (b.disabled = false));
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const modal = document.querySelector(`${SELECTORS.modal}:not([hidden])`);
    if (modal) closeModal(modal);
  });
})();

